import { useTranslation } from 'react-i18next'

import type { UserWorkspace, WorkspaceUser } from '@/api/types'
import { getSession } from '@/features/auth/session-store'
import { UserAvatar } from '@/shared/components/user-avatar'
import { Badge } from '@/shared/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { memberLabel, sortMembers } from '../members'

/**
 * Who is in this workspace, and what can be done about it.
 *
 * Read from the workspace the shell already has rather than from the member
 * endpoint: `GET /api/workspaces/` returns every membership of every workspace the
 * user is in, so a dedicated list would be a second copy of what is already cached,
 * and it would reach a smaller audience -- listing members is admin-only, while the
 * same rows arrive in every member's own payload.
 */
export function MembersDialog({
  workspace,
  open,
  onOpenChange,
}: {
  workspace: UserWorkspace
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const members = sortMembers(workspace.users)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('member.title')}</DialogTitle>
          <DialogDescription>
            {t('member.description', { name: workspace.name })}
          </DialogDescription>
        </DialogHeader>

        {/*
          Read-only, and entirely. Who is here, what they may do, and whether they
          are barred are all the realm's: `core.idp_feed` projects them and nothing
          in this application writes them.
        */}
        <p className="text-sm text-muted-foreground">{t('member.tenantAdd')}</p>

        <ul className="max-h-96 divide-y overflow-y-auto rounded-md border">
          {members.map((member) => (
            <MemberRow key={member.id} member={member} />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

const JOINED = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })

function MemberRow({ member }: { member: WorkspaceUser }) {
  const { t } = useTranslation()
  const currentUserId = getSession().user?.id
  const label = memberLabel(member)

  return (
    <li className="flex flex-wrap items-center gap-3 p-3">
      <UserAvatar src={member.avatar_url} seed={member.email} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate text-sm">
          {label}
          {member.user_id === currentUserId && (
            <Badge variant="secondary">{t('member.you')}</Badge>
          )}
          {member.is_suspended && (
            <Badge variant="destructive">{t('member.suspended')}</Badge>
          )}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {t('member.joined', {
            email: member.email,
            when: JOINED.format(new Date(member.created_on)),
          })}
        </p>
      </div>

      <span className="text-sm text-muted-foreground">
        {member.permissions === 'ADMIN' ? t('member.roleAdmin') : t('member.roleMember')}
      </span>
    </li>
  )
}
